import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CategoryTree = ({ 
  categories, 
  selectedCategory, 
  onCategorySelect, 
  onCategoryMove, 
  onCategoryDelete,
  searchTerm 
}) => {
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [draggedItem, setDraggedItem] = useState(null);
  const dragOverItem = useRef(null);

  const toggleExpanded = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const handleDragStart = (e, category) => {
    setDraggedItem(category);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, category) => {
    e.preventDefault();
    dragOverItem.current = category;
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetCategory) => {
    e.preventDefault();
    if (draggedItem && targetCategory && draggedItem.id !== targetCategory.id) {
      onCategoryMove(draggedItem.id, targetCategory.id);
    }
    setDraggedItem(null);
    dragOverItem.current = null;
  };

  const filterCategories = (categories, searchTerm) => {
    if (!searchTerm) return categories;
    
    return categories.filter(category => 
      category.translations.en.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.translations.ar.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const renderCategory = (category, level = 0) => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expandedCategories.has(category.id);
    const isSelected = selectedCategory?.id === category.id;
    const isDraggedOver = dragOverItem.current?.id === category.id;

    return (
      <div key={category.id} className="select-none">
        <div
          className={`flex items-center py-2 px-3 rounded-md cursor-pointer transition-micro hover:bg-muted group ${
            isSelected ? 'bg-primary text-primary-foreground' : ''
          } ${isDraggedOver ? 'bg-accent/20 border-2 border-accent border-dashed' : ''}`}
          style={{ marginLeft: `${level * 20}px` }}
          onClick={() => onCategorySelect(category)}
          draggable
          onDragStart={(e) => handleDragStart(e, category)}
          onDragOver={(e) => handleDragOver(e, category)}
          onDrop={(e) => handleDrop(e, category)}
        >
          {/* Expand/Collapse Button */}
          <div className="w-6 flex justify-center">
            {hasChildren ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpanded(category.id);
                }}
                className="w-5 h-5 p-0 hover:bg-transparent"
              >
                <Icon 
                  name={isExpanded ? 'ChevronDown' : 'ChevronRight'} 
                  size={14}
                  className={isSelected ? 'text-primary-foreground' : 'text-muted-foreground'}
                />
              </Button>
            ) : (
              <div className="w-5 h-5 flex items-center justify-center">
                <div className="w-1 h-1 bg-muted-foreground rounded-full"></div>
              </div>
            )}
          </div>

          {/* Category Icon */}
          <div className="w-8 h-8 rounded-md bg-surface flex items-center justify-center mr-3">
            <img 
              src={category.image} 
              alt={category.translations.en.name} 
              className="w-full h-full object-cover rounded-md"
            />
          </div>

          {/* Category Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <span className={`font-medium truncate ${
                isSelected ? 'text-primary-foreground' : 'text-foreground'
              }`}>
                {category.translations.en.name} {/* Display English name */}
              </span>
              <span className={`font-medium truncate ${
                isSelected ? 'text-primary-foreground' : 'text-foreground'
              }`}>
                {category.translations.ar.name} {/* Display Arabic name */}
              </span>
              
              {/* Product Count Badge */}
              {category.productCount > 0 && (
                <span className={`text-xs px-2 py-1 rounded-full ml-2 ${
                  isSelected 
                    ? 'bg-primary-foreground/20 text-primary-foreground' 
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {category.productCount}
                </span>
              )}
            </div>
            
            {/* Status Indicator */}
            <div className="flex items-center mt-1">
              <div className={`w-2 h-2 rounded-full mr-2 ${
                category.status === 'active' ? 'bg-success' : 'bg-warning'
              }`}></div>
              <span className={`text-xs ${
                isSelected ? 'text-primary-foreground/80' : 'text-muted-foreground'
              }`}>
                {category.status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="opacity-0 group-hover:opacity-100 transition-micro flex items-center space-x-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                onCategoryDelete(category.id);
              }}
              className="w-6 h-6 p-0 hover:bg-destructive hover:text-destructive-foreground"
            >
              <Icon name="Trash2" size={12} />
            </Button>
          </div>
        </div>

        {/* Children */}
        {hasChildren && isExpanded && (
          <div className="ml-2">
            {category.children.map(child => renderCategory(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const filteredCategories = filterCategories(categories, searchTerm);

  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-1 p-2">
        {filteredCategories.length > 0 ? (
          filteredCategories.map(category => renderCategory(category))
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="Search" size={48} className="mx-auto mb-4 opacity-50" />
            <p>No categories found</p>
            {searchTerm && (
              <p className="text-sm mt-2">Try adjusting your search terms</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryTree;
